import React, { useState, useEffect, useContext } from 'react';
import { useAuth } from '../../context/authContext';
import CountContext from '../../context/countContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { faTrash, faClock, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const StoredRequestsView = () => {
  const authCtx = useAuth();
  const countCtx = useContext(CountContext);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState(null);

  // Clean up expired requests when component mounts and update count
  useEffect(() => {
    authCtx.cleanupExpiredRequests();

    // Update the count for stored requests when component mounts
    if (countCtx.setCount) {
      countCtx.setCount('storedRequests', authCtx.storedRequests.length);
    }
  }, [authCtx.cleanupExpiredRequests, countCtx.setCount, authCtx.storedRequests.length]);

  const formatTimestamp = timestamp => {
    const date = new Date(timestamp);
    return date.toLocaleString('de-DE');
  };

  const handleRetryRequest = requestId => {
    const request = authCtx.getStoredRequest(requestId);
    if (!request) return;

    // Auth context already runs retryStoredRequestWithRequest and removeStoredRequest in onAuthenticationSuccess.
    // This callback is only for optional extra UI logic after success (e.g. refresh); do not retry again.
    authCtx.showAuthModalForRequest(requestId, null, request);
  };

  const handleCancelRequest = request => {
    setRequestToCancel(request);
    setShowCancelDialog(true);
  };

  const confirmCancelRequest = () => {
    if (requestToCancel) {
      authCtx.removeStoredRequest(requestToCancel.id);
    }
    setShowCancelDialog(false);
    setRequestToCancel(null);
  };

  const clearAllRequests = () => {
    authCtx.clearAllStoredRequests();
  };

  if (authCtx.storedRequests.length === 0) {
    return (
      <Box sx={{ ...viewRootTextPrimarySx, p: 3 }}>
        <Typography variant='h5' gutterBottom>
          Gespeicherte Anfragen
        </Typography>
        <Alert severity='info'>Es sind keine gespeicherten Anfragen vorhanden.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='h5'>
          Gespeicherte Anfragen ({authCtx.storedRequests.length})
        </Typography>
        <Button
          variant='outlined'
          color='error'
          onClick={clearAllRequests}
          startIcon={<FontAwesomeIcon icon={faTrash} />}
        >
          Alle löschen
        </Button>
      </Box>

      <Grid container spacing={2}>
        {authCtx.storedRequests.map(request => (
          <Grid item xs={12} md={6} lg={4} key={request.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2,
                  }}
                >
                  <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                    {request.displayInfo?.Typ || 'Anfrage'}
                  </Typography>
                  <Tooltip title='Anfrage abbrechen'>
                    <IconButton
                      size='small'
                      color='error'
                      onClick={() => handleCancelRequest(request)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                >
                  <FontAwesomeIcon icon={faClock} style={{ marginRight: '4px' }} />
                  {formatTimestamp(request.timestamp)}
                </Typography>

                <Box sx={{ mb: 2, flex: 1 }}>
                  {!request.displayInfo ||
                  (Object.keys(request.displayInfo).length === 1 &&
                    Object.keys(request.displayInfo)[0] === 'Anfrage') ? (
                    // Fallback: show technical details when no displayInfo or only generic info is available
                    <>
                      <Typography variant='caption' display='block' sx={{ mb: 0.5 }}>
                        <strong>Methode:</strong> {request.method || 'GET'}
                      </Typography>
                      <Typography variant='caption' display='block' sx={{ mb: 0.5 }}>
                        <strong>URL:</strong> {request.url}
                      </Typography>
                      {request.body &&
                        Object.keys(request.body).length > 0 &&
                        Object.entries(request.body).map(([key, value]) => (
                          <Typography key={key} variant='caption' display='block' sx={{ mb: 0.5 }}>
                            <strong>{key}:</strong>{' '}
                            {typeof value === 'object' ? JSON.stringify(value) : value}
                          </Typography>
                        ))}
                    </>
                  ) : (
                    // Show displayInfo key-value pairs
                    Object.entries(request.displayInfo).map(([key, value]) => {
                      if (key === 'Typ') return null; // Skip type as it's already shown as header
                      return (
                        <Typography key={key} variant='caption' display='block' sx={{ mb: 0.5 }}>
                          <strong>{key}:</strong> {value}
                        </Typography>
                      );
                    })
                  )}
                </Box>

                <Box sx={{ mt: 'auto' }}>
                  <Button
                    variant='contained'
                    size='small'
                    fullWidth
                    onClick={() => handleRetryRequest(request.id)}
                    startIcon={<CloudUploadIcon />}
                  >
                    Anfrage wiederholen
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)}>
        <DialogTitle>Anfrage abbrechen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du diese Anfrage wirklich abbrechen? Diese Aktion kann nicht rückgängig gemacht
            werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>Abbrechen</Button>
          <Button onClick={confirmCancelRequest} color='error' variant='contained'>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoredRequestsView;
